import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import VoiceCard from "@/components/VoiceCard";
import VoiceFilters from "@/components/VoiceFilters";
import { Loader2, SearchX } from "lucide-react";

const EMPTY = { q: "", language: "", country: "", gender: "", age: "", voice_type: "human" };

// Shared, reusable voice directory: filters + results grid.
// `initial` seeds the filter values (e.g. { language: "French" }) and is used
// as the reset baseline so language landing pages stay scoped after a reset.
export default function VoiceDirectory({ initial = {} }) {
  const [filters, setFilters] = useState({});
  const [values, setValues] = useState({ ...EMPTY, ...initial });
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  // Re-scope when navigating between language landing pages.
  useEffect(() => { setValues({ ...EMPTY, ...initial }); /* eslint-disable-next-line */ }, [initial.language]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(values).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/voices", { params });
      setVoices(data);
      if (window.gtag) window.gtag("event", "search_performed");
    } catch (e) {} finally { setLoading(false); }
  }, [values]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="voice-directory">
      <div className="sticky top-16 z-30 mb-8">
        <VoiceFilters filters={filters} values={values} onChange={setValues}
          onReset={() => setValues({ ...EMPTY, ...initial })} hideTypeToggle />
      </div>

      <div className="mb-5 flex items-center justify-between">
        <span data-testid="results-count" className="text-sm font-semibold text-zinc-500">
          {loading ? "Laden…" : `${voices.length} stem${voices.length === 1 ? "" : "men"}`}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-brand-violet"><Loader2 className="animate-spin" size={32} /></div>
      ) : voices.length === 0 ? (
        <div className="flex flex-col items-center py-24 text-center text-zinc-500">
          <SearchX size={40} className="mb-3" />
          <p className="font-semibold">Geen stemmen voldoen aan je filters.</p>
          <p className="text-sm">Niet ons volledige talentenbestand staat online. <a href="/casting" className="text-brand-violet">Laat ons voor je casten.</a></p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {voices.map((t) => <VoiceCard key={t.id} talent={t} />)}
        </div>
      )}
    </div>
  );
}
